/**
 * V2 Button — themed to V2 tokens, faithful to the `.btn` family in
 * design_reference/styles/app.css. Variants: primary/secondary/ghost/
 * danger/icon; sizes sm/md. Primary uses the --accent fill gradient with
 * --accent-tx text.
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const buttonVariants = cva(
  cn(
    'inline-flex items-center gap-[7px] rounded-[var(--r-md)] font-[530] whitespace-nowrap',
    'border transition-[background,border-color,transform] duration-[120ms]',
    'outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
    'active:translate-y-[0.5px] disabled:opacity-50 disabled:pointer-events-none',
    '[&_svg]:w-[15px] [&_svg]:h-[15px]',
  ),
  {
    variants: {
      variant: {
        // .btn default (secondary)
        secondary: cn(
          'bg-[var(--bg-3)] text-[var(--tx-hi)] border-[var(--bd)]',
          'shadow-[inset_0_1px_0_var(--hi-top),var(--sh-sm)]',
          'hover:bg-[var(--bg-4)] hover:border-[var(--bd-strong)]',
        ),
        // .btn.primary — `v2-btn-primary` hook lets brand skins (e.g. Citizens)
        // override the gradient via :root[data-brand="…"] .v2-btn-primary.
        primary: cn(
          'v2-btn-primary',
          'bg-[linear-gradient(180deg,var(--accent-hi),var(--accent))] text-[var(--accent-tx)] font-semibold',
          'border-[color-mix(in_oklab,var(--accent)_60%,black)]',
          'shadow-[inset_0_1px_0_rgba(255,255,255,.28),0_1px_2px_rgba(0,0,0,.4)]',
          'hover:brightness-[1.06] [&_svg]:text-[var(--accent-tx)]',
        ),
        // .btn.ghost
        ghost: cn(
          'bg-transparent border-transparent text-[var(--tx-mid)] shadow-none',
          'hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
        ),
        // .btn.danger (base + danger hover)
        danger: cn(
          'bg-[var(--bg-3)] text-[var(--tx-hi)] border-[var(--bd)]',
          'shadow-[inset_0_1px_0_var(--hi-top),var(--sh-sm)]',
          'hover:text-[var(--bad)] hover:border-[color-mix(in_oklab,var(--bad)_40%,transparent)]',
        ),
        // .btn.icon — square; pair with a size for dimensions
        icon: cn(
          'bg-[var(--bg-3)] text-[var(--tx-hi)] border-[var(--bd)] justify-center p-0',
          'shadow-[inset_0_1px_0_var(--hi-top),var(--sh-sm)]',
          'hover:bg-[var(--bg-4)] hover:border-[var(--bd-strong)]',
        ),
      },
      size: {
        // .btn (md)
        md: 'h-8 px-3 text-[12.5px]',
        // .btn.sm
        sm: 'h-7 px-[9px] text-[12px]',
      },
    },
    compoundVariants: [
      { variant: 'icon', size: 'md', class: 'w-8 px-0' },
      { variant: 'icon', size: 'sm', class: 'w-7 px-0' },
    ],
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

/** Props for the V2 Button. */
export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as a child element (Radix Slot) instead of a `<button>`. */
  asChild?: boolean;
}

/** V2 button. See `buttonVariants` for variant/size APIs. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        type={asChild ? undefined : (type ?? 'button')}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
